import React, { FC } from 'react';
import { ScrollView, View } from 'react-native';
import { ProductionOrderSearchItem } from '@interfaces/search';
import { scale as initialScale } from '@components/frame';
import {
  DETAILS_SCREENS_CONTAINER_STYLES_FOR_SCROLL_VIEW,
  SCREEN_BORDER_RADIUS,
} from '../../../../../common/constants/base.constants';
import Loading from '@components/loading';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import ColorWrapper, { WithColor } from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import { BlockTitle } from '@v2/pages/machines/modules/now/components/Job/components/JobContent';
import { isPhone, isWeb } from '@helpers';
import { useManageProductionOrderContent } from './hooks/useManageProductionOrderContent';
import {
  getErpInfoBlockConfig,
  getInfoBlockConfig,
  getJobsConfig,
  getScheduleBlockConfig,
  getTopBlockConfig,
} from './helpers';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface ProductionOrderContentProps extends WithColor {
  productionOrder: ProductionOrderSearchItem;
  maxWidth: number;
  loading: boolean;
  bg?: string;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const ProductionOrderContent: FC<ProductionOrderContentProps> = ({
  maxWidth,
  productionOrder,
  loading,
  C,
  bg = 'Background/Light/Field',
  scale = initialScale,
  font = initialFont,
}) => {
  const { jobs, onProductionOrderImageClick, onPartNumberClick, onJobLineClick } = useManageProductionOrderContent({ productionOrder });

  const shadowStyles = isWeb ? getBoxShadowStyle({ type: 'Field', C }) : {};

  const blockStyles = [{ borderRadius: scale(SCREEN_BORDER_RADIUS) }, shadowStyles];

  const topBlockConfig = getTopBlockConfig({ productionOrder, C, scale, font, loading });
  const scheduleBlockConfig = getScheduleBlockConfig({ productionOrder, C, font });
  const infoBlock = getInfoBlockConfig({ productionOrder, C, scale, font, onPartNumberClick });
  const jobsConfig = getJobsConfig({ jobs, C, scale, font, bg, loading, onJobLineClick });
  const erpInfoBlockConfig = getErpInfoBlockConfig({ productionOrder, C, font });

  return (
    <View
      style={[
        {
          marginInline: 'auto',
          width: '100%',
          flex: 1,
        },
        maxWidth != null && { maxWidth },
      ]}
    >
      {loading && (
        <View testID={TEST_IDS.productionOrderContent.loading} style={{ marginTop: scale(40) }}>
          <Loading background="transparent" />
        </View>
      )}
      {!loading && productionOrder && (
        <ScrollView
          contentContainerStyle={[DETAILS_SCREENS_CONTAINER_STYLES_FOR_SCROLL_VIEW, { gap: scale(20), paddingBottom: scale(20) }]}
          showsVerticalScrollIndicator={false}
        >
          <View testID={TEST_IDS.productionOrderContent.topBlock} style={blockStyles}>
            <ImagesContainer
              {...{
                maxDetailsWidth: maxWidth,
                panelViewMode: false,
                mode: ImagesContainerMode.ROW,
                documentRetrieveKey: DOCUMENT_ENTITY_TYPE.ProductionOrder,
                documentRetrieveId: productionOrder?.productionOrderId,
                onImagePress: onProductionOrderImageClick,
                scale,
                font,
              }}
            />
            {topBlockConfig.map((d) => (
              <React.Fragment key={d.i.name}>
                <InputLine {...{ ...d, bg, C, scale, font }} />
              </React.Fragment>
            ))}
          </View>

          <View>
            <BlockTitle C={C} testID={TEST_IDS.productionOrderContent.blockTitle('SCHEDULE')} title="SCHEDULE" scale={scale} font={font} />
            <View style={blockStyles}>
              {scheduleBlockConfig.map((d) => (
                <React.Fragment key={d.i.name}>
                  <InputLine {...{ ...d, bg, C, loading, scale, font }} />
                </React.Fragment>
              ))}
            </View>
          </View>

          <View style={blockStyles}>
            {infoBlock.map((d) => (
              <React.Fragment key={d.i.name}>
                <InputLine {...{ ...d, bg, C, loading, scale, font }} />
              </React.Fragment>
            ))}
          </View>

          {isPhone && (
            <View>
              <BlockTitle C={C} testID={TEST_IDS.productionOrderContent.blockTitle('JOBS')} title="JOBS" scale={scale} font={font} />
              <View style={blockStyles}>
                {jobsConfig?.map(({ key, ...j }) => (
                  <React.Fragment key={key}>
                    <InputLine {...j} scale={scale} font={font} disableFlexConfForValueComponentMobile />
                  </React.Fragment>
                ))}
              </View>
            </View>
          )}

          <View>
            <BlockTitle C={C} testID={TEST_IDS.productionOrderContent.blockTitle('ERP INFORMATION')} title="ERP INFORMATION" scale={scale} font={font} />
            <View style={blockStyles}>
              {erpInfoBlockConfig.map((d) => (
                <React.Fragment key={d.i.name}>
                  <InputLine {...{ ...d, bg, C, loading, scale, font }} />
                </React.Fragment>
              ))}
            </View>
          </View>

          <View style={blockStyles}>
            <InputLine
              {...{
                testID: TEST_IDS.productionOrderContent.allErpFieldsButton,
                borderBottomRadius: true,
                borderTopRadius: true,
                onlyLabel: true,
                data: [{}],
                index: 0,
                scale,
                font,
                i: {
                  name: 'All ERP Fields',
                  action: () => {},
                },
                C,
                loading,
              }}
            />
          </View>
        </ScrollView>
      )}
    </View>
  );
};

export default ColorWrapper(ProductionOrderContent);
